import React, { useEffect, useContext, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { ThemeProvider, ThemeContext } from "./context/ThemeContext";
import { estaAutenticado } from "./utils/auth";

import "./styles/requiredFields.css";

import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import MatriculaForm from "./components/MatriculaForm";

import CadastroImoveis from "./pages/CadastroImoveis";
import SocietarioEmpresas from "./pages/SocietarioEmpresas";
import SocietarioSocios from "./pages/SocietarioSocios";
import RelatorioImoveis from "./pages/RelatorioImoveis";
import CAR from "./pages/CAR";
import CCIR from "./pages/CCIR";
import CCIRAnual from "./pages/CCIRAnual";
import DITR from "./pages/DITR";
import DITRAnual from "./pages/DITRAnual";
import IPTU from "./pages/IPTU";
import IPTUAnual from "./pages/IPTUAnual";
import Locatarios from "./pages/Locatarios";
import RelatorioLocatarios from "./pages/RelatorioLocatarios";
import UnidadesLocaveis from "./pages/UnidadesLocaveis";
import RelogioEnergia from "./pages/RelogioEnergia";
import RelogioAgua from "./pages/RelogioAgua";
import Cidades from "./pages/Cidades";
import IndicesReajuste from "./pages/IndicesReajuste.jsx";
import Cartorios from "./pages/Cartorios";
import Status from "./pages/Status";

import EnquadramentoPages from "./pages/EnquadramentoPages";

import Usuarios from "./pages/Usuarios";
import Proprietarios from "./pages/Proprietarios";
import GrupoProprietario from "./pages/GrupoProprietario";
import RepresentanteLegal from "./pages/RepresentanteLegal";
import CadastroTemplateCT from "./pages/CadastroTemplateCT";
import Complemento from "./pages/Complemento";
import LogsAtividades from "./pages/LogsAtividades";
import AlcadasAprovacao from "./pages/AlcadasAprovacao";
import AlcadasAssinaturas from "./pages/AlcadasAssinaturas";
import Auditoria from "./pages/Auditoria";
import DocusignAssinaturas from "./pages/DocusignAssinaturas";
import PropostaComercial from "./pages/PropostaComercial";
import DashboardContratos from "./pages/DashboardContratos";
import Contratos from "./pages/Contratos";
import SocietarioAlteracoes from "./pages/SocietarioAlteracoes";
import SocietarioEtapasFluxo from "./pages/SocietarioEtapasFluxo";
import FichaCadastro from "./pages/FichaCadastro";
import PreencherFichaPublica from "./pages/PreencherFichaPublica";
import Templates from "./pages/Templates";
import EmDesenvolvimento from "./pages/EmDesenvolvimento";

import CentralInteracao from "./components/CentralInteracao";

import Login from "./pages/Login";
import ValidarProposta from "./pages/ValidarProposta";

function Layout() {
  const location = useLocation();
  const { temaEscuro, alternarTema } = useContext(ThemeContext);

  const [nomeUsuario, setNomeUsuario] = useState("");
  const [emailUsuario, setEmailUsuario] = useState("");
  const [tipoUsuario, setTipoUsuario] = useState("");

  const [showPerfil, setShowPerfil] = useState(false);
  const [showTrocarSenha, setShowTrocarSenha] = useState(false);
  const [menuRecolhido, setMenuRecolhido] = useState(() => {
    return localStorage.getItem("menuLateralRecolhido") === "1";
  });

  useEffect(() => {
    localStorage.setItem("menuLateralRecolhido", menuRecolhido ? "1" : "0");
  }, [menuRecolhido]);

  useEffect(() => {
    const root = window.document.documentElement;
    temaEscuro ? root.classList.add("dark") : root.classList.remove("dark");
  }, [temaEscuro]);

  useEffect(() => {
    fetch("/api/teste")
      .then(async (res) => {
        const text = await res.text();

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${text.slice(0, 120)}`);
        }

        return JSON.parse(text);
      })
      .then((data) => {
        console.log("Backend respondeu:", JSON.stringify(data, null, 2));
      })
      .catch((err) => {
        console.error("Erro ao conectar com o backend:", err);
      });
  }, []);

  useEffect(() => {
    // Em rota pública não há Header/Sidebar para alimentar — e, pior, um token
    // vencido no localStorage daria 403 aqui e redirecionaria para o login,
    // derrubando quem abriu um link público.
    if (ehRotaPublica(location.pathname)) return;

    const tk = localStorage.getItem("token");

    if (!tk) {
      console.warn("Sem token no localStorage (usuário não logado ainda).");
      return;
    }

    fetch("/api/usuarios/me", {
      headers: {
        Authorization: `Bearer ${tk}`,
      },
    })
      .then(async (res) => {
        const text = await res.text();

        if (!res.ok) {
          if (res.status === 403) {
            console.warn("Sessão expirada, removendo token.");
            localStorage.removeItem("token");
            window.location.href = "/";
            return;
          }

          throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
        }

        return JSON.parse(text);
      })
      .then((data) => {
        if (!data) return;

        if (data.nome) setNomeUsuario(data.nome);
        if (data.email) setEmailUsuario(data.email);
        if (data.tipo) setTipoUsuario(data.tipo);
      })
      .catch((err) => {
        console.error("Erro ao buscar dados do usuário logado:", err);
      });
  }, [location.pathname]);

  const isPublicPage = ehRotaPublica(location.pathname);

  return (
    <div className="flex min-h-screen bg-gray-100 dark:bg-gray-900 text-black dark:text-white relative">
      {!isPublicPage && !menuRecolhido && (
        <aside className="w-64 bg-[#1c1f26] shadow-md transition-all duration-300">
          <Sidebar />
        </aside>
      )}

      <div className="flex flex-col flex-1 min-w-0">
        {!isPublicPage && (
          <Header
            username={nomeUsuario}
            email={emailUsuario}
            tipo={tipoUsuario}
            toggleTheme={alternarTema}
            isDarkMode={temaEscuro}
            setShowPerfil={setShowPerfil}
            setShowTrocarSenha={setShowTrocarSenha}
            menuRecolhido={menuRecolhido}
            toggleMenu={() => setMenuRecolhido((prev) => !prev)}
          />
        )}

        <main className={`flex-1 pt-20 p-6 overflow-auto relative ${showPerfil || showTrocarSenha ? "backdrop-blur-sm" : ""}`}>
          <Routes>
            <Route path="/" element={<Login />} />

            <Route path="/validar-proposta/:token" element={<ValidarProposta />} />

            {/* preenchimento da ficha por link público — sem login */}
            <Route path="/ficha-publica/:token" element={<PreencherFichaPublica />} />

            <Route path="/home" element={estaAutenticado() ? <CentralInteracao modo="pagina" /> : <Navigate to="/" />} />
            <Route path="/matricula-form" element={estaAutenticado() ? <MatriculaForm /> : <Navigate to="/" />} />

            <Route path="/matriculas-imoveis" element={estaAutenticado() ? <CadastroImoveis /> : <Navigate to="/" />} />

            <Route path="/relatorio-imoveis" element={estaAutenticado() ? <RelatorioImoveis /> : <Navigate to="/" />} />

            <Route path="/car" element={estaAutenticado() ? <CAR /> : <Navigate to="/" />} />

            <Route path="/ccir" element={estaAutenticado() ? <CCIR /> : <Navigate to="/" />} />
            <Route path="/ccir-anual" element={estaAutenticado() ? <CCIRAnual /> : <Navigate to="/" />} />

            <Route path="/ditr" element={estaAutenticado() ? <DITR /> : <Navigate to="/" />} />

            <Route path="/ditr-anual" element={estaAutenticado() ? <DITRAnual /> : <Navigate to="/" />} />
            <Route path="/iptu" element={estaAutenticado() ? <IPTU /> : <Navigate to="/" />} />

            <Route path="/iptu-anual" element={estaAutenticado() ? <IPTUAnual /> : <Navigate to="/" />} />

            <Route path="/locatarios" element={estaAutenticado() ? <Locatarios /> : <Navigate to="/" />} />

            <Route path="/relatorio-locatarios" element={estaAutenticado() ? <RelatorioLocatarios /> : <Navigate to="/" />} />

            <Route path="/unidades-locaveis" element={estaAutenticado() ? <UnidadesLocaveis /> : <Navigate to="/" />} />

            <Route path="/relogio-energia" element={estaAutenticado() ? <RelogioEnergia /> : <Navigate to="/" />} />

            <Route path="/relogio-agua" element={estaAutenticado() ? <RelogioAgua /> : <Navigate to="/" />} />

            <Route path="/cidades" element={estaAutenticado() ? <Cidades /> : <Navigate to="/" />} />

            <Route path="/indices-reajuste" element={estaAutenticado() ? <IndicesReajuste /> : <Navigate to="/" />} />

            <Route
              path="/areas-construidas/cadastro-tipos-areas/:slug"
              element={estaAutenticado() ? <EmDesenvolvimento secao="CADASTRO TIPOS ÁREAS" /> : <Navigate to="/" />}
            />

            <Route path="/areas-construidas/lancamentos/:slug" element={estaAutenticado() ? <EmDesenvolvimento secao="LANÇAMENTOS" /> : <Navigate to="/" />} />

            <Route
              path="/unidades-locaveis/:slug"
              element={estaAutenticado() ? <EmDesenvolvimento tituloPrincipal="UNIDADES LOCÁVEIS" secao="CADASTRO" /> : <Navigate to="/" />}
            />

            <Route
              path="/workflow-contrato-locacao/:slug"
              element={estaAutenticado() ? <EmDesenvolvimento tituloPrincipal="WORKFLOW CONTRATO LOCAÇÃO" secao="CONTRATO LOCAÇÃO" /> : <Navigate to="/" />}
            />

            <Route
              path="/workflow-outros-contratos-interno/:slug"
              element={
                estaAutenticado() ? <EmDesenvolvimento tituloPrincipal="WORKFLOW OUTROS CONTRATOS INTERNO" secao="CONTRATOS INTERNOS" /> : <Navigate to="/" />
              }
            />

            <Route
              path="/workflow-outros-contratos-externo/:slug"
              element={
                estaAutenticado() ? <EmDesenvolvimento tituloPrincipal="WORKFLOW OUTROS CONTRATOS EXTERNO" secao="CONTRATOS EXTERNOS" /> : <Navigate to="/" />
              }
            />

            <Route
              path="/templates-contrato/:slug"
              element={estaAutenticado() ? <EmDesenvolvimento tituloPrincipal="TEMPLATES DE CONTRATO" secao="TEMPLATES" /> : <Navigate to="/" />}
            />

            <Route
              path="/administracao/:slug"
              element={estaAutenticado() ? <EmDesenvolvimento tituloPrincipal="ADMINISTRAÇÃO" secao="CADASTRO" /> : <Navigate to="/" />}
            />
            {/* ALTERAÇÕES CONTRATUAIS */}

            <Route path="/alteracoes-contratuais/empresas" element={estaAutenticado() ? <SocietarioEmpresas /> : <Navigate to="/" />} />

            <Route path="/alteracoes-contratuais/socios" element={estaAutenticado() ? <SocietarioSocios /> : <Navigate to="/" />} />

            <Route path="/alteracoes-contratuais/processos" element={estaAutenticado() ? <SocietarioAlteracoes /> : <Navigate to="/" />} />

            <Route path="/alteracoes-contratuais/etapas" element={estaAutenticado() ? <SocietarioEtapasFluxo /> : <Navigate to="/" />} />
            <Route path="/cartorios" element={estaAutenticado() ? <Cartorios /> : <Navigate to="/" />} />

            <Route path="/status" element={estaAutenticado() ? <Status /> : <Navigate to="/" />} />

            <Route path="/enquadramentos" element={estaAutenticado() ? <EnquadramentoPages /> : <Navigate to="/" />} />

            <Route path="/usuarios" element={estaAutenticado() ? <Usuarios /> : <Navigate to="/" />} />

            <Route path="/proprietarios" element={estaAutenticado() ? <Proprietarios /> : <Navigate to="/" />} />

            <Route path="/grupo-proprietario" element={estaAutenticado() ? <GrupoProprietario /> : <Navigate to="/" />} />

            <Route path="/responsavel-legal" element={estaAutenticado() ? <RepresentanteLegal /> : <Navigate to="/" />} />

            <Route path="/cadastro-template-ct" element={estaAutenticado() ? <CadastroTemplateCT /> : <Navigate to="/" />} />

            <Route path="/complemento" element={estaAutenticado() ? <Complemento /> : <Navigate to="/" />} />

            <Route path="/logs-atividades" element={estaAutenticado() ? <LogsAtividades /> : <Navigate to="/" />} />

            <Route path="/alcadas-assinaturas" element={estaAutenticado() ? <AlcadasAssinaturas /> : <Navigate to="/" />} />

            <Route path="/auditoria" element={estaAutenticado() ? <Auditoria /> : <Navigate to="/" />} />

            <Route path="/dashboard-contratos" element={estaAutenticado() ? <DashboardContratos /> : <Navigate to="/" />} />

            <Route path="/central-interacao" element={estaAutenticado() ? <CentralInteracao modo="pagina" /> : <Navigate to="/" />} />

            <Route path="/proposta-comercial" element={estaAutenticado() ? <PropostaComercial /> : <Navigate to="/" />} />

            <Route path="/contratos" element={estaAutenticado() ? <Contratos /> : <Navigate to="/" />} />

            <Route path="/ficha-cadastro" element={estaAutenticado() ? <FichaCadastro /> : <Navigate to="/" />} />

            <Route path="/ficha-cadastro" element={estaAutenticado() ? <FichaCadastro /> : <Navigate to="/" />} />

            <Route path="/proposta-comercial" element={estaAutenticado() ? <PropostaComercial /> : <Navigate to="/" />} />

            <Route path="/contratos" element={estaAutenticado() ? <Contratos /> : <Navigate to="/" />} />
            <Route path="/alcadas-aprovacao" element={estaAutenticado() ? <AlcadasAprovacao /> : <Navigate to="/" />} />

            <Route path="/auditoria" element={estaAutenticado() ? <Auditoria /> : <Navigate to="/" />} />

            <Route path="/assinatura-final" element={estaAutenticado() ? <DocusignAssinaturas /> : <Navigate to="/" />} />

            <Route path="/templates" element={estaAutenticado() ? <Templates /> : <Navigate to="/" />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

// Rotas que abrem sem login. Usada tanto pelo layout (esconde Sidebar/Header)
// quanto pelo efeito de sessão abaixo.
const ROTAS_PUBLICAS = ["/"];

function ehRotaPublica(pathname) {
  return ROTAS_PUBLICAS.includes(pathname) || pathname.startsWith("/validar-proposta/") || pathname.startsWith("/ficha-publica/");
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <Layout />
      </Router>
    </ThemeProvider>
  );
}

export default App;
